import { Alert, Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { EmissionTier, MetricState, metricStateLabels } from '../common'
import { usePropertyClimateData, type PropertyResult } from '../../../hooks/climateData'
import { useCurrentUser } from '../../../hooks/users'
import { ExtremeWindScoreBadge } from '../common/extremeWind/utils'
import ExtremeWindSpeedTrends from './extremeWind/ExtremeWindSpeedTrends'
import ClimateMetricCard from '../common/ClimateMetricCard'
import ClimateSectionTitle from '../common/ClimateSectionTitle'

type MetricPoint = { value: number | null; year: number | null }

function extractPoint(result: PropertyResult | undefined, tier: EmissionTier): MetricPoint {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) {
    return { value: null, year: null }
  }

  const tierNode = result.data?.data?.[tier] as { x?: number; y?: number } | undefined
  const value = typeof tierNode?.y === 'number' ? tierNode.y : null
  const year = typeof tierNode?.x === 'number' ? tierNode.x : null

  return { value, year }
}

function formatCurrency(value: number | null | undefined, currencySymbol: string) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '–'
  const formatted = value.toLocaleString(undefined, { maximumFractionDigits: 0 })
  return `${currencySymbol}${formatted}`
}

function formatNumber(value: number | null | undefined, digits = 2) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '–'
  return value.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits })
}

const PropertyExtremeWind: React.FC<{ propertyId?: number | string; emissionTier: EmissionTier }> = ({
  propertyId,
  emissionTier,
}) => {
  const numericPropertyId =
    typeof propertyId === 'string' ? (isNaN(Number(propertyId)) ? null : Number(propertyId)) : propertyId

  if (!numericPropertyId) {
    return <Alert severity="info">Select a property to view extreme wind metrics.</Alert>
  }

  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData('tc:score', { propertyId: numericPropertyId })

  const {
    data: lossData,
    isLoading: lossLoading,
    error: lossError,
  } = usePropertyClimateData('tc:average_damage_loss', { propertyId: numericPropertyId })

  const {
    data: biData,
    isLoading: biLoading,
    error: biError,
  } = usePropertyClimateData('tc:average_bi_days', { propertyId: numericPropertyId })
  const { currencySymbol } = useCurrentUser()

  const loading = scoreLoading || lossLoading || biLoading
  const allErrored = !!(scoreError && lossError && biError)
  const hasData = !!(scoreData || lossData || biData)

  if (loading && !hasData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Unable to load extreme wind metrics for this property.</Alert>
  }

  if (!hasData) {
    return <Alert severity="info">No extreme wind data is available for this property.</Alert>
  }

  const scorePoint = extractPoint(scoreData, emissionTier)
  const lossPoint = extractPoint(lossData, emissionTier)
  const biPoint = extractPoint(biData, emissionTier)

  const metrics = [
    {
      key: 'score',
      title: 'Risk Rating',
      description: 'From very low to very high',
      state: scoreData?.state?.value as MetricState | undefined,
      year: scorePoint.year,
      render: () => <ExtremeWindScoreBadge score={scorePoint.value} size="regular" />,
    },
    {
      key: 'loss',
      title: 'Damage Loss',
      description: 'Expected annual damage loss from extreme wind',
      state: lossData?.state?.value as MetricState | undefined,
      year: lossPoint.year,
      render: () => (
        <Box>
          <Typography variant="h3" component="div">
            {formatCurrency(lossPoint.value, currencySymbol)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            per year
          </Typography>
        </Box>
      ),
    },
    {
      key: 'bi',
      title: 'Business Interruption',
      description: 'Average number of downtime days caused by extreme winds',
      state: biData?.state?.value as MetricState | undefined,
      year: biPoint.year,
      render: () => (
        <Box>
          <Typography variant="h3" component="div">
            {formatNumber(biPoint.value, 1)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            days / year
          </Typography>
        </Box>
      ),
    },
  ]

  return (
    <Stack spacing={4}>
      <Card>
        <CardContent>
          <Box mb={3}>
            <ClimateSectionTitle title="Overview" subtitle="Summary of average extreme wind risk till the end of century" />
          </Box>

          <Grid container spacing={4}>
            {metrics.map(metric => (
              <Grid item xs={12} md={4} key={metric.key}>
                <ClimateMetricCard title={metric.title} variant="outlined">
                  <Box mb={2}>
                    {metric.state === MetricState.SUCCEEDED ? (
                      metric.render()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {metric.state ? metricStateLabels[metric.state] : 'Data unavailable'}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">{metric.description}</Typography>
                </ClimateMetricCard>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <ExtremeWindSpeedTrends propertyId={numericPropertyId as number} emissionTier={emissionTier} />
        </CardContent>
      </Card>
    </Stack>
  )
}

export default PropertyExtremeWind
